/**
 * `PROG-003`/`SHELL-054`: what "persist the result" actually writes into the
 * save document. Deliberately not in `progression.ts` alongside
 * `isLevelUnlocked` -- `PROG-053`'s own Inspection verification is that file
 * importing nothing shaped like a `GameModule`, checked by reading the whole
 * file, not just the one function the requirement is about. `GameResult`
 * (`@contracts/game-module`) is exactly that shape, so it gets its own file
 * rather than putting a future reader in the position of re-deriving which
 * import in `progression.ts` was the one `PROG-053` actually meant.
 */

import type { GameResult } from '@contracts/game-module';
import type { Goal } from '@contracts/level';
import { goalKey, goalSetKey } from '@contracts/level';
import type { GameProgress, LevelRecord, SaveDocument } from '@contracts/save';

/**
 * `PROG-042`. One star for `completed` (`PROG-041` -- the level's required
 * goals met is exactly what that field means), one more for every optional
 * goal set ever banked. Takes the two numbers already computed rather than
 * a `GameResult`/`Level` pair: stars are permanent (`PROG-043`), so the
 * count a player should see is the save document's own cumulative state,
 * not something re-derived from one attempt. `ResultScreen.tsx` and
 * `recordCompletion` below both read this off the one `LevelRecord`
 * `recordCompletion` just produced, so the number shown and the number
 * persisted can never drift into two formulas.
 *
 * Clamped to three (`PROG-040`), sprint-8b stage 1 -- `DATA-069` limits a
 * level to two optional sets, so `completedGoalSetCount` should never
 * exceed two in practice, but "should never" is not "cannot": a stale,
 * not-yet-pruned `completedGoalSets` (a legacy migration entry, or a set a
 * since-edited level no longer defines -- see `recordCompletion`'s own
 * pruning below) can hand this a count that would otherwise overshoot.
 * This clamp is what keeps the range true unconditionally, including on a
 * screen that only has the save document in hand and never loads the level
 * to prune against (level select, once `SHELL-031` builds it) -- pruning
 * happens where the level is available; the ceiling holds everywhere,
 * because it depends on nothing but the two numbers already in hand.
 */
export function deriveStars(completed: boolean, completedGoalSetCount: number): number {
  return completed ? Math.min(3, 1 + completedGoalSetCount) : 0;
}

/**
 * Whether `goal` is satisfied by `result`, generically across every kind --
 * shell-safe (`@contracts` only, `PROG-053`'s own boundary), unlike
 * match-3's own `logic/goals.ts` `isGoalComplete`, which this file must not
 * import. `avoid` inverts the usual direction (a violation count staying at
 * or under `limit` is success, not progress toward one); `time` reads the
 * attempt's own `durationMs` rather than a `goalProgress` entry, since
 * nothing tracks "time" as a per-goal unit.
 */
function isGoalSatisfied(goal: Goal, result: GameResult): boolean {
  switch (goal.kind) {
    case 'score':
      return result.score >= goal.target;
    case 'collect':
    case 'clear-obstacle':
      return (result.goalProgress[goalKey(goal)] ?? 0) >= goal.count;
    case 'custom':
      return (result.goalProgress[goalKey(goal)] ?? 0) >= goal.target;
    case 'avoid':
      return (result.goalProgress[goalKey(goal)] ?? 0) <= goal.limit;
    case 'time':
      return result.durationMs <= goal.limitMs;
  }
}

/**
 * `PROG-046`: a set counts as complete only when every goal in it is
 * satisfied inside this one, *won* attempt -- never partial credit banked
 * across two attempts, which is exactly the exploit a prohibition
 * (`avoid`) goal would otherwise open (SDD-003 "Stars and completion").
 * Returns `goalSetKey` identities for every set of `optionalGoals` newly
 * satisfied by `result` alone; merging that with whatever is already
 * banked is `recordCompletion`'s job, not this function's -- this one has
 * no memory of a previous attempt.
 */
function newlyCompletedSetKeys(
  optionalGoals: readonly (readonly Goal[])[] | undefined,
  result: GameResult,
): string[] {
  if (!optionalGoals || result.outcome !== 'won') return [];
  const keys: string[] = [];
  for (const set of optionalGoals) {
    if (set.length > 0 && set.every((goal) => isGoalSatisfied(goal, result))) {
      keys.push(goalSetKey(set));
    }
  }
  return keys;
}

/**
 * Attempts count regardless of outcome (`PROG-045`); `completed`,
 * `completedGoalSets`, `bestScore` and `bestTimeMs` are all monotonic
 * across replays (`PROG-043`/`044`/`048`) -- a later, worse attempt must
 * not un-bank a goal set any more than it may reduce a best score.
 * `completedGoalSets` is a union, not a replace: keys this attempt
 * satisfies are added to whatever was already banked, deduplicated and
 * sorted for a deterministic persisted shape, never cleared outright.
 *
 * It is filtered first, though, against the level's *current*
 * `optionalGoals` -- sprint-8b stage 1: an entry that matches no set the
 * level defines today (a legacy migration entry from the old
 * positional-index scheme, or a set a since-edited level no longer
 * defines) is dropped rather than carried forward forever. This is what
 * makes such an entry temporary rather than permanent, and it can only
 * happen here, where the level's real `optionalGoals` are in hand -- the
 * migration that produces a legacy entry (`migrateV2ToV3`,
 * `save-guards.ts`) has no access to level files, and a screen with only
 * the save document (level select, once `SHELL-031` builds it) has no
 * level to filter against either. `deriveStars`'s own clamp is what keeps
 * a star count showing correctly everywhere in the meantime, independent
 * of whether pruning has happened yet.
 *
 * `optionalGoals` comes from the level currently being played, not the
 * save document -- `Play.tsx`'s own `level.optionalGoals`, the same
 * `Level` the level file itself declares. A level with none (`undefined`,
 * equivalent to an empty array per that field's own doc comment) simply
 * has no valid keys to filter toward, which correctly drops every
 * previously-banked entry for a level that no longer defines any set at
 * all -- there is nothing left for such an entry to mean.
 *
 * `bestTimeMs` (`PROG-048`) only ever updates on a *won* attempt -- a level
 * never completed has no completion time, which is exactly what keeps this
 * `null` rather than a value from a losing or abandoned run. Fastest time
 * wins, the same "best across replays" shape `bestScore` already has.
 */
export function recordCompletion(
  progress: GameProgress | undefined,
  levelId: string,
  result: GameResult,
  optionalGoals: readonly (readonly Goal[])[] | undefined,
): GameProgress {
  const existing = progress?.levels[levelId];
  const won = result.outcome === 'won';
  const newlyCompleted = newlyCompletedSetKeys(optionalGoals, result);
  const validKeys = new Set((optionalGoals ?? []).map(goalSetKey));
  const carried = (existing?.completedGoalSets ?? []).filter((key) => validKeys.has(key));
  const completedGoalSets = [...new Set([...carried, ...newlyCompleted])].sort();
  const record: LevelRecord = {
    completed: Boolean(existing?.completed) || won,
    completedGoalSets,
    bestScore: Math.max(existing?.bestScore ?? 0, result.score),
    bestTimeMs: won
      ? existing?.bestTimeMs === null || existing?.bestTimeMs === undefined
        ? result.durationMs
        : Math.min(existing.bestTimeMs, result.durationMs)
      : (existing?.bestTimeMs ?? null),
    attempts: (existing?.attempts ?? 0) + 1,
    firstCompletedAt: existing?.firstCompletedAt ?? (won ? new Date().toISOString() : null),
  };
  return {
    levels: { ...progress?.levels, [levelId]: record },
    lastPlayedLevel: levelId,
  };
}

/**
 * `PROG-008`/`PWA-052`: "when the player first completes a level" -- read
 * from the document rather than a dedicated flag, since adding one to
 * `SaveDocument` is a schema change (Sprint 8's own job, per the roadmap,
 * not this stage's). No level anywhere has `attempts > 0` before the very
 * first `recordCompletion` call ever succeeds, so the document itself
 * already answers the question. Called with the document as it stood
 * *before* this attempt's own `persist`, from `Play.tsx`'s `onComplete` --
 * after that write, the check would always be false, including for the
 * attempt it is meant to catch.
 */
export function isFirstCompletionEver(document: SaveDocument): boolean {
  return Object.values(document.games).every((progress) =>
    Object.values(progress.levels).every((record) => record.attempts === 0),
  );
}
